import { useAuth } from "AuthContext";

import { SideNavLayout } from "@filadd/phi-plus-layout";
import { css } from "@filadd/phi-plus-styled-system/css";
import { Navbar } from "components/layout/Navbar";
import { useCourseSlug } from "hooks/course";
import { useCourse } from "hooks/courses-new/course";
import { useRouter } from "next/router";
import { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { InstallmentNotificationBanner } from "./InstallmentNotificationBanner";

interface Props
  extends Pick<ComponentProps<typeof SideNavLayout>, "layoutFeatures"> {
  children: JSX.Element;
  layoutName: string;
  ssrCookies: Record<string, string>;
}

const MainLayout = ({
  children,
  layoutFeatures,
  layoutName,
  ssrCookies,
}: Props) => {
  const router = useRouter();
  const courseSlug = useCourseSlug();
  const { user, loginUrl, logoutUrl } = useAuth();
  const {
    data: courseData,
    isLoading: isCourseLoading,
    error: isCourseError,
  } = useCourse({ params: { field: "slug", value: courseSlug } });

  const userReleases = user?.releases ?? [];
  const locale = router.locale ?? "es-AR";
  const { t: scopedSideNavT } = useTranslation("side-nav");
  const isFixed: boolean = layoutName === "unit_content_view_layout";

  if (isCourseLoading) {
    return <div>...</div>;
  }

  if (isCourseError || !courseData) {
    return <div>Layout error: Failed to fetch course data</div>;
  }

  return (
    <SideNavLayout
      ssrCookies={ssrCookies}
      fixed={isFixed}
      layoutFeatures={layoutFeatures}
      goBack={() => router.back()}
      releases={userReleases}
      courseTitle={courseData.title}
      locale={locale}
      t={scopedSideNavT}
      topBar={
        <Navbar userData={user} loginUrl={loginUrl} logoutUrl={logoutUrl} />
      }
    >
      <main
        className={css({
          flexGrow: 1,
          width: "full",
          display: "flex",
          pt: 2,
          pb: 4,
          flexDirection: "column",
          overflowX: "hidden",
        })}
      >
        <InstallmentNotificationBanner />
        {children}
      </main>
    </SideNavLayout>
  );
};

export default MainLayout;
